import { safeJSONParse } from 'utils/data/parse.utils';

export function isValidPolygon(polygon) {
  // Check if polygon is an array
  if (!Array.isArray(polygon)) {
    return false;
  }

  // Check if every element in the polygon array is an array with exactly two elements
  for (let i = 0; i < polygon.length; i++) {
    if (!Array.isArray(polygon[i]) || polygon[i].length !== 2) {
      return false;
    }
  }

  // If all checks passed, return true
  return true;
}

export function flattenPolygon(polygon) {
  // If the polygon is valid, return it as is
  if (isValidPolygon(polygon)) {
    return polygon;
  }

  // If the polygon is not valid, try to flatten it
  if (Array.isArray(polygon) && polygon.length > 0 && Array.isArray(polygon[0])) {
    return flattenPolygon(polygon[0]);
  }

  return null;
}

////////////////////////
// Coordinate Parsing
// Used to parse coordinates from strings and convert into decimal format
////////////////////////

//Checks if a string is in the format of degrees, minutes, seconds
//Input: String
//Output: Boolean
function isValidDMS(coord) {
  const DMSFormatRegex =
    /^(-?\d{1,3}°\s?\d{1,2}'\s?\d{1,2}(\.\d+)?["″]\s?[NSEW]?),\s?(-?\d{1,3}°\s?\d{1,2}'\s?\d{1,2}(\.\d+)?["″]\s?[NSEW]?)$/;

  return DMSFormatRegex.test(coord);
}

//Checks if a string is in the format of decimal
//Input: String
//Output: Boolean
function isValidDecimalCoord(coord) {
  const decimalFormatRegex = /^(-?\d+(\.\d+)?)\s*,?\s*(-?\d+(\.\d+)?)$/;
  const decimalAndDirectionRegex = /^(-?\d+(\.\d+)?)°?\s?[NSEW],\s?(-?\d+(\.\d+)?)°?\s?[NSEW]$/;

  let isDecimal = decimalFormatRegex.test(coord) || decimalAndDirectionRegex.test(coord);

  return isDecimal;
}

//Can be called to check if a string is a valid coordinate
//Input: String
//Output: Boolean
export function stringIsCoordinates(string) {
  const isCoordinates = isValidDMS(string) || isValidDecimalCoord(string);
  return isCoordinates;
}

function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}

export function calculateDistanceInMiles(A1, A2, B1, B2) {
  const earthRadiusMiles = 3958.8; // Earth radius in miles

  const lat1 = toRadians(A1);
  const lon1 = toRadians(A2);
  const lat2 = toRadians(B1);
  const lon2 = toRadians(B2);

  const deltaLat = lat2 - lat1;
  const deltaLon = lon2 - lon1;

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const distance = earthRadiusMiles * c;
  return distance;
}

////////////////////////
// Polygon Utilities
// Used for point-in-polygon testing and jurisdiction boundary checking
////////////////////////

/**
 * Normalizes any supported polygon format into { polygons, center, bounds }.
 * Supports:
 *   - Already-parsed array (single polygon or multipolygon)
 *   - JSON string
 *   - Space-separated "lat,lng lat,lng ..." string
 *
 * Returns null if the input cannot be resolved to a valid polygon.
 *
 * @param {any} polygon
 * @returns {{ polygons: [number, number][][], center: [number, number], bounds: [[number, number], [number, number]] } | null}
 */
export default function normalizePolygon(polygon) {
  if (!polygon) return null;

  let parsed;
  if (Array.isArray(polygon)) {
    parsed = polygon;
  } else if (typeof polygon === 'string') {
    parsed = safeJSONParse(polygon, null);
    if (!Array.isArray(parsed)) {
      const polyArr = polygon
        .trim()
        .split(/\s+/)
        .map((pair) => pair.split(',').map(Number));
      if (
        polyArr.length > 2 &&
        polyArr.every((pt) => pt.length === 2 && !isNaN(pt[0]) && !isNaN(pt[1]))
      ) {
        parsed = polyArr;
      } else {
        return null;
      }
    }
  } else {
    return null;
  }

  // A single polygon is [[lat,lng], ...]; a multipolygon is [[[lat,lng], ...], ...]
  const isMultiPolygon =
    Array.isArray(parsed) && Array.isArray(parsed[0]) && Array.isArray(parsed[0][0]);
  const polygons = isMultiPolygon ? parsed : [parsed];

  if (!polygons.length) return null;

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  for (const poly of polygons) {
    for (const [lat, lng] of poly) {
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
    }
  }

  if (
    !Number.isFinite(minLat) ||
    !Number.isFinite(maxLat) ||
    !Number.isFinite(minLng) ||
    !Number.isFinite(maxLng)
  ) {
    return null;
  }

  return {
    polygons,
    center: [(minLat + maxLat) / 2, (minLng + maxLng) / 2],
    bounds: [
      [minLat, minLng],
      [maxLat, maxLng],
    ],
  };
}
